import "./FeatureFooter.scss";

import { FC, useEffect, useState } from "react";
import { Doc } from "sharedb/lib/client";
import { twMerge } from "tailwind-merge";

import { WorksheetType } from "../../../../shared/types/worksheet";
import { BLOCKS_PATH } from "../../../../shared/worksheets/constants";
import { getEditableBlocks } from "../../../../shared/worksheets/doc-helpers";

import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { LegacyButton } from "mds/components/LegacyButton";
import { ToggleSwitch } from "mds/components/ToggleSwitch";
import { ChevronLeftIcon, ChevronRightIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { usePageActions } from "utils/page";
import { featurePage, usePresentation } from "utils/presentation";
import { getNavigationValues } from "utils/urls";

export type FeatureFooterProps = {
  page: PageType;
  doc?: Doc<WorksheetType>;
  className?: string;
};

const getBlocks = (doc: Doc<WorksheetType>, isRollup: boolean) =>
  (isRollup ? getEditableBlocks(doc) : doc?.data?.[BLOCKS_PATH]) || [];

export const FeatureFooter: FC<FeatureFooterProps> = ({ doc, className, page }) => {
  const {
    presentedSessionId,
    presentedAccessId,
    presentedBlockId,
    presentingRollup,
    presentingOutcomes,
    isPresenting,
  } = usePresentation();

  // TODO: If we don't conflict with assessments we could use the content navigation subnav
  // but right now the PageOverview component uses subnav for assetIds with assessments
  const [blocks, setBlocks] = useState(getBlocks(doc, presentingRollup));
  const blockIds = blocks.map((b) => b.id);

  const { canAuthorCourse } = useCapabilities();
  const blockId = presentedBlockId;
  const blockIndex = blocks?.findIndex((b) => b.id === blockId);
  const showBlockNavigation =
    isPresenting && canAuthorCourse && blockIndex > -1 && blocks.length > 1;
  const showAnonymousToggle =
    isPresenting &&
    canAuthorCourse &&
    presentingRollup &&
    !presentingOutcomes &&
    (!presentedBlockId || !presentedAccessId);

  const { savePage } = usePageActions();

  const { nextId, prevId } = getNavigationValues(blockIds, { currentId: blockId });

  useEffect(() => {
    setBlocks(getBlocks(doc, presentingRollup));
  }, [doc, presentingRollup]);

  const changeFeatureBlock = (newBlockId: string) => {
    featurePage(presentedSessionId, page, {
      blockId: newBlockId,
      accessId: presentedAccessId,
      isRollup: presentingRollup,
    });
  };

  const toggleAnonymousResponses = () => {
    savePage({ id: page.id, anonymize_student_responses: !page.anonymize_student_responses });
  };

  if (!showAnonymousToggle && !showBlockNavigation) {
    return <div className="md:h-5" />;
  }

  return (
    <div
      className={twMerge(
        "feature-footer flex min-h-6 w-full items-center justify-center gap-2 px-4 py-2",
        showBlockNavigation && "justify-between",
        showAnonymousToggle && "min-h-[60px]",
        className,
      )}
    >
      {showBlockNavigation && (
        <LegacyButton
          aria-label={t("presentation.previous_block")}
          disabled={!prevId}
          eventName="Feature Modal - Previous block"
          kind="tertiary"
          size="xs"
          onClick={() => changeFeatureBlock(prevId)}
        >
          <ChevronLeftIcon />
          {t("presentation.previous_block")}
        </LegacyButton>
      )}

      {showAnonymousToggle && (
        <ToggleSwitch
          className="rounded-lg border border-white bg-black-tint-97 px-3 py-[5px] text-sm font-medium text-black-tint-20 data-[selected]:border-blue-tint-40 data-[selected]:bg-blue-tint-90"
          eventName="Feature Modal - Toggle anonymization"
          isSelected={!page.anonymize_student_responses}
          labelStart={t("presentation.show_student_names")}
          onClick={toggleAnonymousResponses}
        />
      )}

      {showBlockNavigation && (
        <>
          {!showAnonymousToggle && (
            <div className="body-xs text-black-tint-55">
              {blockIndex + 1}/{blocks.length}
            </div>
          )}

          <LegacyButton
            aria-label={t("presentation.next_block")}
            disabled={!nextId}
            eventName="Feature Modal - Next block"
            kind="tertiary"
            size="xs"
            onClick={() => changeFeatureBlock(nextId)}
          >
            {t("presentation.next_block")}
            <ChevronRightIcon />
          </LegacyButton>
        </>
      )}
    </div>
  );
};
